/**
 * Implements the authorisation flow described here
 * https://serato.atlassian.net/wiki/spaces/PROD/pages/7443284934/Authorization+Code+Flow+with+Proof+Key+for+Code+Exchange+PKCE
 */
import Sws from '@serato/sws-sdk'
import type { SwsConfiguration } from '@serato/sws-sdk'
import { useAuthStore } from '@/stores/auth-store'
import { useSpinnerStore } from '@/stores/spinner-store'
import { parseToken, type RefreshToken, parseJwtToken } from '@/utils/token-parser'

interface StorageKeys {
  refreshToken: string
  accessToken: string
  state: string
  codeVerifier: string
  redirectUrl: string
}

export interface CheckoutSws extends Sws {
  /**
   * Exchanges an authorization code, with its associated state variable for a bearer token
   *
   * @param code Authorization code returned from SSO
   * @param state State variable returned from SSO
   * @returns Promise resolved when code exchange was attepted, or returned early due to invalid parameters
   */
  exchangeAuthorizationCode(code: string, state: string): Promise<void>
  login(refreshTokenId?: string, redirectUrl?: string): Promise<void>
  logout(): void
}

/**
 * Extended SWS client
 *
 * Adds the following to a base SWS client instance:
 *
 * - Uses a storage mechanism to persist authorization credentials during
 *   an authorization request workflow to the SWS Identity Service.
 * - Parses the page URL and, if it contains authorization-related URL parameters,
 *   process the authorization request.
 */
export default class extends Sws implements CheckoutSws {
  private storage: Storage
  private storageKeys: StorageKeys
  private spinnerStore: ReturnType<typeof useSpinnerStore>

  constructor ({ appId, secret = '', timeout = 3000, serviceUri = {} }: SwsConfiguration, storage: Storage) {
    super({ appId, secret, timeout, serviceUri })
    const ENV_NAME = import.meta.env.VITE_APP_ENV_NAME
    const envNameKey = ENV_NAME ? ENV_NAME + '.' : ''
    this.storage = storage
    this.spinnerStore = useSpinnerStore()
    this.storageKeys = {
      accessToken: 'auth.accessToken.' + envNameKey + appId,
      refreshToken: 'auth.refreshToken.' + envNameKey + appId,
      state: 'auth.state.' + envNameKey + appId,
      codeVerifier: 'auth.codeVerifier.' + envNameKey + appId,
      redirectUrl: 'auth.redirectUrl.' + envNameKey + appId
    }

    this.accessTokenUpdatedHandler = (accessToken: string, _, refreshToken: string) => {
      this.setTokens(accessToken, refreshToken)
    }

    this.setInvalidRefreshTokenHandler(async () => {
      // Leave the spinner on until the browser redirects to the ID service. This is required because the browser handles
      // redirects via window.location.href asynchronously and has no guarantees about when it will do so.
      this.spinnerStore.incrementLoadingCount()
      await this.login()
      // Throw an error to prevent promise resolution while redirecting to the login page
      throw new Error('Invalid refresh token')
    })

    this.setPasswordReEntryRequiredHandler(async () => {
      // Leave the spinner on until the browser redirects to the ID service. This is required because the browser handles
      // redirects via window.location.href asynchronously and has no guarantees about when it will do so.
      this.spinnerStore.incrementLoadingCount()

      const refreshToken = this.storage.getItem(this.storageKeys.refreshToken)
      if (refreshToken) {
        const refreshTokenUuid = parseToken<RefreshToken>(refreshToken).uuid
        if (refreshTokenUuid) {
          // Force the user to re-enter their password
          await this.login(refreshTokenUuid)
          return
        }
      }
      // Force the user to re-login since there is no refresh token and therefore
      // it is impossible to confirm their password
      this.logout()
    })

    const refreshToken = this.storage.getItem(this.storageKeys.refreshToken)
    if (refreshToken !== null) {
      this.refreshToken = refreshToken
    }

    const accessToken = this.storage.getItem(this.storageKeys.accessToken)
    if (accessToken !== null) {
      // If we have an access token but no refresh token, logout to prevent broken auth state
      if (!refreshToken) {
        this.logout()
        return
      }

      this.accessToken = accessToken

      this.setUserData()
    }
  }

  public async exchangeAuthorizationCode (code: string, state: string) {
    if (code === '' || state !== this.storage.getItem(this.storageKeys.state)) {
      return
    }

    try {
      // Exchange `code`, `redirectUrl` and `codeVerifier` for tokens
      const userLogin = await this.id.tokenExchange(
        code,
        this.storage.getItem(this.storageKeys.redirectUrl) ?? '',
        this.storage.getItem(this.storageKeys.codeVerifier) ?? ''
      )

      // Flush the old data in store
      this.authReset()

      this.setTokens(userLogin.tokens.access.token, userLogin.tokens.refresh.token)
    } catch (e) {
      this.authReset()
    }
  }

  /**
   * Sets access and refresh tokens, and sets refresh token in storage.
   *
   * @param accessToken
   * @param refreshToken
   */
  private setTokens (accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    this.storage.setItem(this.storageKeys.accessToken, this.accessToken)
    this.storage.setItem(this.storageKeys.refreshToken, this.refreshToken)

    this.setUserData()
  }

  private setUserData (): void {
    const jwtPayload = parseJwtToken(this.accessToken)
    if (jwtPayload) {
      const authStore = useAuthStore()
      authStore.user.email = jwtPayload.email
      authStore.user.id = jwtPayload.uid
    }
  }

  /**
   * Starts an authorization request and redirects the user agent
   * to an authorization URL on the SWS Identity Service.
   *
   * @param refreshTokenId Optional. Refresh token ID of the signed in user, if we want the user to reenter their
   * password to authorize the application.
   * @param redirectUrl Optional. URL to redirect to after successful login. It will be set to the current URL if
   * not specified.
   */
  public async login (refreshTokenId?: string, redirectUrl?: string) {
    // Delete the refresh token and access token in local storage
    this.deleteTokens()

    redirectUrl ??= window.location.href
    const authorizationRequest = await this.createAuthorizationRequest(redirectUrl, refreshTokenId)

    // Write items to storage
    this.storage.setItem(this.storageKeys.state, authorizationRequest.state)
    this.storage.setItem(this.storageKeys.codeVerifier, authorizationRequest.codeVerifier)
    this.storage.setItem(this.storageKeys.redirectUrl, redirectUrl)

    window.location.href = authorizationRequest.url
  }

  public async changeAccount (refreshTokenId?: string) {
    // Delete the refresh token and access token in local storage
    this.deleteTokens()

    // create /auth redirect url
    const redirectUrl = window.location.href
    const authorizationRequest = await this.createAuthorizationRequest(redirectUrl, refreshTokenId)

    // Write items to storage
    this.storage.setItem(this.storageKeys.state, authorizationRequest.state)
    this.storage.setItem(this.storageKeys.codeVerifier, authorizationRequest.codeVerifier)
    this.storage.setItem(this.storageKeys.redirectUrl, redirectUrl)

    // log out first and then redirect to /auth on ID service
    window.location.href = this.serviceUri.id + '/en/logout' + '?app_id=' + this.appId + '&continue=' +
      encodeURIComponent(authorizationRequest.url + '&flow_params={"entry":"checkout"}')
  }

  public logout () {
    // Delete the tokens from the local storage
    this.deleteTokens()

    // Redirect to the id service to log out
    window.location.href = this.serviceUri.id + '/en/logout?app_id=' + this.appId + '&continue=' + window.location.href
  }

  private deleteTokens () {
    this.storage.removeItem(this.storageKeys.refreshToken)
    this.storage.removeItem(this.storageKeys.accessToken)
  }

  /**
   * Stops an authorization request.
   */
  private authReset () {
    // Invalidate transient items in storage
    this.storage.removeItem(this.storageKeys.state)
    this.storage.removeItem(this.storageKeys.codeVerifier)
    this.storage.removeItem(this.storageKeys.redirectUrl)
  }
}
